/**
 * The single source of truth for AIChess sounds.
 *
 * Every semantic game event maps to exactly one bundled file. The bundled pack
 * lives in `public/sounds/` and is generated by `scripts/generate-sounds.mjs`,
 * so the repository ships original audio and never has to hot-link a
 * third-party CDN on every move.
 *
 * `CHESSCOM_FILES` is an *optional* alternative pack: the settings screen lets a
 * user point the app at the public Chess.com sound files at runtime. Those files
 * are not redistributed with AIChess - the browser fetches them directly - and
 * entries are only listed where the file name is known to exist. Anything
 * missing falls back to the bundled pack.
 */

export type SoundId =
  | 'moveSelf'
  | 'moveOpponent'
  | 'capture'
  | 'castle'
  | 'check'
  | 'moveSelfCheck'
  | 'moveOpponentCheck'
  | 'promote'
  | 'premove'
  | 'illegal'
  | 'gameStart'
  | 'gameEnd'
  | 'gameWin'
  | 'gameLose'
  | 'gameDraw'
  | 'notify'
  | 'click'
  | 'tenSeconds';

/** Bundled files, relative to `<base>/sounds/`. */
export const SOUND_FILES: Record<SoundId, string> = {
  moveSelf: 'move-self.wav',
  moveOpponent: 'move-opponent.wav',
  capture: 'capture.wav',
  castle: 'castle.wav',
  check: 'move-check.wav',
  moveSelfCheck: 'move-self-check.wav',
  moveOpponentCheck: 'move-opponent-check.wav',
  promote: 'promote.wav',
  premove: 'premove.wav',
  illegal: 'illegal.wav',
  gameStart: 'game-start.wav',
  gameEnd: 'game-end.wav',
  gameWin: 'game-win-long.wav',
  gameLose: 'game-lose-long.wav',
  gameDraw: 'game-draw.wav',
  notify: 'notify.wav',
  click: 'click.wav',
  tenSeconds: 'tenseconds.wav',
};

export type SoundPack = 'builtin' | 'chesscom';

export const CHESSCOM_BASE = 'https://images.chesscomfiles.com/chess-themes/sounds/_MP3_/default/';

/**
 * Mapping onto the public Chess.com sound files. Only file names that are known
 * to exist are listed; every other id falls back to the bundled pack.
 */
export const CHESSCOM_FILES: Partial<Record<SoundId, string>> = {
  moveSelf: 'move-self.mp3',
  moveOpponent: 'move-opponent.mp3',
  capture: 'capture.mp3',
  castle: 'castle.mp3',
  check: 'move-check.mp3',
  moveSelfCheck: 'move-self-check.mp3',
  moveOpponentCheck: 'move-opponent-check.mp3',
  promote: 'promote.mp3',
  premove: 'premove.mp3',
  illegal: 'illegal.mp3',
  gameStart: 'game-start.mp3',
  gameEnd: 'game-end.mp3',
  gameWin: 'game-win-long.mp3',
  gameLose: 'game-lose-long.mp3',
  gameDraw: 'game-draw.mp3',
  notify: 'notify.mp3',
  click: 'click.mp3',
  tenSeconds: 'tenseconds.mp3',
};

/* ------------------------------------------------- matching game events */

/** Everything the sound system needs to know about one finished move. */
export interface MoveSoundEvent {
  /** Who made the move. Never the piece type: a pawn move sounds like any other move. */
  actor: 'player' | 'opponent';
  isCapture: boolean;
  isCastle: boolean;
  isPromotion: boolean;
  isCheck: boolean;
  isCheckmate: boolean;
  isDraw: boolean;
  /** From the human player's point of view; used for the result sound. */
  playerWon?: boolean;
}

export interface SoundPlan {
  /** Played as soon as the move lands. */
  immediate: SoundId;
  /** Played after the move has had time to land (result fanfares). */
  follow?: { id: SoundId; delayMs: number };
}

/**
 * One move -> one sound. Priority is checkmate > draw > check > capture >
 * castle > promotion > ordinary move, and the ordinary move is chosen by *who*
 * moved, never by which piece moved.
 *
 * Returned as a plain value so it can be unit tested without a DOM.
 */
export function planMoveSounds(event: MoveSoundEvent): SoundPlan {
  const checking: SoundId =
    event.actor === 'player' ? 'moveSelfCheck' : 'moveOpponentCheck';

  if (event.isCheckmate) {
    return {
      immediate: checking,
      follow: {
        id:
          event.playerWon === true
            ? 'gameWin'
            : event.playerWon === false
              ? 'gameLose'
              : 'gameEnd',
        delayMs: 320,
      },
    };
  }
  if (event.isDraw) return { immediate: 'gameDraw' };
  if (event.isCheck) return { immediate: checking };
  if (event.isCapture) return { immediate: 'capture' };
  if (event.isCastle) return { immediate: 'castle' };
  if (event.isPromotion) return { immediate: 'promote' };
  return { immediate: event.actor === 'player' ? 'moveSelf' : 'moveOpponent' };
}

/** Sound for a game that ended without a move (resignation, timeout, load). */
export function planResultSound(
  result: string | null,
  playerColor: 'w' | 'b'
): SoundId {
  if (result === 'white') return playerColor === 'w' ? 'gameWin' : 'gameLose';
  if (result === 'black') return playerColor === 'b' ? 'gameWin' : 'gameLose';
  if (result === 'resign') return 'gameLose';
  return 'gameDraw';
}
